import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CircleOff, CircleCheck, Plus, Search, SlidersHorizontal, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { decodeFilters, decodeSort, encodeFilters, encodeSort, itemListColumns } from "@patronix/domain/list";
import { DataList, DetailPanel, DetailPlaceholder, type Column, type ListView } from "@/components/DataList";
import type { MassTarget } from "@/components/MassPanel";
import { ItemMassPanel, type ItemMassKind } from "@/features/items/ItemMass";
import { Badge } from "@/components/ui";
import {
  itemDetailQuery,
  itemKeys,
  itemListQuery,
  itemTypes,
  listItems,
  uomListQuery,
  type ItemRow,
  type ItemTypeFilter,
} from "@/features/items/api";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { ImportPanel } from "@/features/import/ImportPanel";
import { ItemPanel } from "@/features/items/ItemPanel";
import { isItemTab, type ItemTab } from "@/features/items/itemTabs";
import { cn } from "@/lib/cn";
import { useActiveCompany } from "@/lib/activeCompany";
import { useCan } from "@/lib/session";

interface Search {
  type: ItemTypeFilter;
  q: string;
  inactive: boolean;
  item?: string | undefined;
  /** With item=new: prefill the new item from this one (toolbar Duplicate). */
  copy?: string | undefined;
  /** Import panel open for this entity (replaces the item detail). */
  import?: "items" | "item_conversions" | "item_planning" | "item_sales" | "customer_items" | "item_purchase" | "supplier_items" | undefined;
  /** Department tab of the item detail (ADR-046). */
  tab?: ItemTab | undefined;
  /** Column filters (encodeFilters) and sort ("-description"). */
  f?: string | undefined;
  s?: string | undefined;
}

const typeFilters: ItemTypeFilter[] = ["all", ...itemTypes];

export const Route = createFileRoute("/_app/master-data/items")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {
      type: typeFilters.includes(raw.type as ItemTypeFilter) ? (raw.type as ItemTypeFilter) : "all",
      q: typeof raw.q === "string" ? raw.q.slice(0, 100) : "",
      inactive: raw.inactive === true || raw.inactive === "true",
    };
    if (typeof raw.item === "string" && raw.item) s.item = raw.item;
    if (s.item === "new" && typeof raw.copy === "string" && raw.copy) s.copy = raw.copy;
    if (isItemTab(raw.tab) && raw.tab !== "general") s.tab = raw.tab;
    if (raw.import === "items" || raw.import === "item_conversions" || raw.import === "item_planning" || raw.import === "item_sales" || raw.import === "customer_items" ||
      raw.import === "item_purchase" || raw.import === "supplier_items")
      s.import = raw.import;
    const f = encodeFilters(decodeFilters(itemListColumns, raw.f));
    if (f) s.f = f;
    const sort = encodeSort(decodeSort(itemListColumns, raw.s));
    if (sort) s.s = sort;
    return s;
  },
  loaderDeps: ({ search }) => listParams(search),
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(uomListQuery);
    return context.queryClient.ensureInfiniteQueryData(itemListQuery(deps));
  },
  component: ItemsPage,
});

function listParams(s: Search) {
  return {
    type: s.type,
    q: s.q,
    inactive: s.inactive,
    filters: decodeFilters(itemListColumns, s.f),
    sort: decodeSort(itemListColumns, s.s),
  };
}

function ItemsPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canManage = useCan("master.item.manage");
  const canImport = useCan("core.data.import");
  const canPlan = useCan("planning.mrp.run");
  const { company } = useActiveCompany();
  // Mass operation open in the detail area (checked rows or the whole filter).
  const [mass, setMass] = useState<{ kind: ItemMassKind; target: MassTarget; count: number | null } | null>(null);

  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const params = listParams(search);
  const query = useInfiniteQuery(itemListQuery(params));
  const view: ListView = {
    spec: itemListColumns,
    query: { filters: params.filters, sort: params.sort },
    onChange: (next) => setSearch({ f: encodeFilters(next.filters) || undefined, s: encodeSort(next.sort) || undefined }),
  };

  const columns: Column<ItemRow>[] = [
    {
      key: "code",
      header: t("items.colCode"),
      width: "minmax(120px,0.9fr)",
      cell: (r, selected) => <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.code}</span>,
    },
    {
      key: "description",
      header: t("items.colDescription"),
      width: "minmax(200px,2fr)",
      cell: (r) => <span className={cn(!r.is_active && "text-ink-muted line-through decoration-ink-faint")}>{r.description}</span>,
    },
    {
      key: "type",
      header: t("items.colType"),
      width: "minmax(130px,1fr)",
      cell: (r) => <Badge tone={typeTone[r.item_type]}>{t(`itemType.${r.item_type}`)}</Badge>,
      options: itemTypes.map((v) => ({ value: v, label: t(`itemType.${v}`) })),
    },
    {
      key: "procurement",
      header: t("items.procurement"),
      width: "minmax(110px,0.7fr)",
      className: "text-ink-muted",
      options: [
        { value: "purchase", label: t("items.purchase") },
        { value: "manufacture", label: t("items.manufacture") },
      ],
      cell: (r) => (r.procurement_type === "purchase" ? t("items.purchase") : t("items.manufacture")),
    },
    { key: "uom", header: t("items.colUom"), width: "minmax(64px,0.4fr)", cell: (r) => r.base_uom?.code ?? "—" },
    {
      key: "tracking",
      header: t("items.colTracking"),
      width: "minmax(110px,0.7fr)",
      className: "text-ink-muted",
      options: [
        { value: "none", label: t("items.trackingNone") },
        { value: "lot", label: t("items.trackingLot") },
        { value: "serial", label: t("items.trackingSerial") },
      ],
      cell: (r) =>
        [r.lot_controlled && t("items.trackingLot"), r.serial_controlled && t("items.trackingSerial")].filter(Boolean).join(" · ") || "—",
    },
  ];

  // Same columns as the list, with stable codes (re-importable, ADR-022).
  const exportColumns: ExportColumn<ItemRow>[] = [
    { header: t("items.colCode"), kind: "code", value: (r) => r.code },
    { header: t("items.colDescription"), kind: "text", value: (r) => r.description },
    { header: t("items.colType"), kind: "code", value: (r) => r.item_type },
    { header: t("items.colUom"), kind: "code", value: (r) => r.base_uom?.code },
    { header: t("items.trackingLot"), kind: "boolean", value: (r) => r.lot_controlled },
    { header: t("items.trackingSerial"), kind: "boolean", value: (r) => r.serial_controlled },
    { header: t("export.active"), kind: "boolean", value: (r) => r.is_active },
  ];

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.masterData.title")} <span aria-hidden="true">/</span> {t("nav.masterData.items")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("items.title")}</h1>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setSearch({ item: "new", copy: undefined, import: undefined })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("items.newItem")}
          </button>
        )}
      </header>

      <Toolbar search={search} setSearch={setSearch} />

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("items.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.item}
          onSelect={(r) => setSearch({ item: r.id, copy: undefined, import: undefined })}
          onClear={() => setSearch({ item: undefined, copy: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(itemDetailQuery(r.id))}
          emptyTitle={t("items.emptyTitle")}
          emptyBody={t("items.emptyBody")}
          emptyAction={
            <button
              type="button"
              onClick={() => setSearch({ type: "all", q: "", inactive: false, item: undefined, f: undefined, s: undefined })}
              className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
            >
              {t("list.clearFilters")}
            </button>
          }
          minWidth={780}
          view={view}
          onClearView={() => setSearch({ type: "all", q: "", inactive: false, f: undefined, s: undefined })}
          tools={{
            id: "items",
            title: t("items.title"),
            onNew: canManage ? () => setSearch({ item: "new", copy: undefined, import: undefined }) : undefined,
            newLabel: t("items.newItem"),
            onDuplicate: canManage ? (r) => setSearch({ item: "new", copy: r.id, import: undefined }) : undefined,
            export: {
              fileBase: "items",
              sheet: t("items.title"),
              columns: exportColumns,
              fetchPage: (cursor, signal) => listItems(params, cursor, signal, EXPORT_PAGE),
            },
            onImport: canManage && canImport ? () => setSearch({ import: "items", item: undefined, copy: undefined }) : undefined,
            mass: [
              ...(canPlan && company
                ? [{ key: "planning", label: t("mass.items.planning"), icon: <SlidersHorizontal size={15} /> }]
                : []),
              ...(canManage
                ? [
                    { key: "deactivate", label: t("mass.items.deactivate"), icon: <CircleOff size={15} /> },
                    { key: "activate", label: t("mass.items.activate"), icon: <CircleCheck size={15} /> },
                  ]
                : []),
            ].map((a) => ({
              ...a,
              open: (target: MassTarget, count: number | null) => {
                setSearch({ item: undefined, import: undefined, copy: undefined });
                setMass({ kind: a.key as ItemMassKind, target, count });
              },
            })),
          }}
        />
        <DetailPanel
          label={mass ? t(`mass.items.${mass.kind}Title`) : search.import ? t("import.title") : t("items.detailLabel")}
          open={!!mass || !!search.item || !!search.import}
          placeholder={<DetailPlaceholder title={t("items.selectPrompt")} hint={t("items.selectPromptHint")} />}
        >
          {mass ? (
            <ItemMassPanel
              key={`${mass.kind}:${mass.target.kind}:${mass.count ?? "all"}`}
              kind={mass.kind}
              target={mass.target}
              count={mass.count}
              list={params}
              companyId={company?.id}
              onClose={() => setMass(null)}
            />
          ) : search.import ? (
            <ImportPanel
              entities={
                canPlan
                  ? ["items", "item_conversions", "item_purchase", "supplier_items", "item_sales", "customer_items", "item_planning"]
                  : ["items", "item_conversions", "item_purchase", "supplier_items", "item_sales", "customer_items"]
              }
              initial={search.import}
              companyId={company?.id}
              onClose={() => setSearch({ import: undefined })}
              invalidate={[itemKeys.all, ["planning"], ["master", "item-sales"], ["master", "item-purchase"]]}
            />
          ) : (
            search.item && (
              <ItemPanel
                key={`${search.item}:${search.copy ?? ""}`}
                itemId={search.item}
                copyFrom={search.copy}
                onClose={() => setSearch({ item: undefined })}
                onCreated={(id) => setSearch({ item: id, copy: undefined })}
                tab={search.tab ?? "general"}
                onTab={(tab) => setSearch({ tab: tab === "general" ? undefined : tab })}
              />
            )
          )}
        </DetailPanel>
      </div>
    </div>
  );
}

const typeTone = {
  material: "neutral",
  semi_finished: "violet",
  finished_good: "success",
  service: "info",
} as const;

function Toolbar({ search, setSearch }: { search: Search; setSearch: (p: Partial<Search>) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState(search.q);

  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div role="group" aria-label={t("items.colType")} className="flex flex-wrap gap-2">
        {typeFilters.map((f) => {
          const active = search.type === f;
          return (
            <button
              key={f}
              type="button"
              aria-pressed={active}
              onClick={() => setSearch({ type: f })}
              className={cn(
                "h-8 rounded-full border px-3 text-[13px] transition-colors",
                active ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
              )}
            >
              {f === "all" ? t("items.filterAll") : t(`itemType.${f}`)}
            </button>
          );
        })}
      </div>
      <label className="ml-1 flex items-center gap-2 text-[13px] text-ink-soft">
        <input
          type="checkbox"
          checked={search.inactive}
          onChange={(e) => setSearch({ inactive: e.target.checked })}
          className="size-4 accent-(--accent)"
        />
        {t("items.showInactive")}
      </label>
      <div className="grow" />
      <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
        <Search size={15} aria-hidden="true" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("items.searchPlaceholder")}
          aria-label={t("items.searchPlaceholder")}
          data-list-search
          className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
        />
        {text && (
          <button type="button" aria-label={t("list.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
            <X size={14} />
          </button>
        )}
      </label>
    </div>
  );
}
